// Pillar D — post-Customer-Merge duplicate-Patient-groups list (AC35/36).
import React, { FC } from "react";
import { useIntl } from "react-intl";
import Box from "../../Unknown/Box";
import Typography from "../../Unknown/Typography";
import useDuplicatePatientGroups, {
  DuplicatePatientGroup,
} from "./useDuplicatePatientGroups";
import DuplicatePatientGroupCard from "./DuplicatePatientGroupCard";
import messages from "./messages";

type DuplicatePatientGroupsListProps = {
  duplicatePatientGroups: DuplicatePatientGroup[];
  selectedCustomerId: string;
  selectedCustomerName: string;
  onPatientClick: (mrn: string) => void;
  // AC37/38 — "View appointment N" links in the Conflict state, opens in a
  // new tab.
  onAppointmentClick: (appointmentId: string) => void;
};

const DuplicatePatientGroupsList: FC<DuplicatePatientGroupsListProps> = ({
  duplicatePatientGroups,
  selectedCustomerId,
  selectedCustomerName,
  onPatientClick,
  onAppointmentClick,
}) => {
  const { formatMessage } = useIntl();
  const {
    groups,
    fieldSelections,
    relationship,
    setRelationship,
    setFieldSelection,
    isSubmitting,
    isAnyExpanded,
    expandGroup,
    submitGroup,
    canSubmitExpanded,
    FIELD_KEYS,
  } = useDuplicatePatientGroups(duplicatePatientGroups);

  // AC47 — a merged group's row stays visible (replaced by a success alert),
  // it doesn't disappear. But the "N duplicate patient(s) ... must also be
  // merged" heading describes what's still OUTSTANDING, not the original
  // snapshot count — once every group has resolved (success or error), the
  // heading no longer applies and should not keep claiming there's
  // unfinished work.
  const pendingCount = groups.filter((g) => !g.outcome).length;

  return (
    <Box>
      {pendingCount > 0 && (
        <>
          <Typography variant="h6" mb={0}>
            {formatMessage(messages.duplicateGroupsTitle, {
              count: pendingCount,
            })}
          </Typography>
          <Typography variant="body2" color="text.secondary" mb="24px">
            {formatMessage(messages.duplicateGroupsSubtitle, {
              count: pendingCount,
            })}
          </Typography>
        </>
      )}
      <Box display="flex" flexDirection="column" gap="24px">
        {groups.map(
          ({
            group,
            key,
            patients,
            isExpanded,
            hasConflict,
            conflictingAppointmentIds,
            outcome,
          }) => (
            <DuplicatePatientGroupCard
              key={key}
              group={group}
              patients={patients}
              hasConflict={hasConflict}
              conflictingAppointmentIds={conflictingAppointmentIds}
              isExpanded={isExpanded}
              isAnyExpanded={isAnyExpanded}
              isSubmitting={isSubmitting}
              outcome={outcome}
              fieldSelections={fieldSelections}
              relationship={relationship}
              fieldKeys={FIELD_KEYS}
              canSubmit={patients ? canSubmitExpanded(patients) : false}
              customerName={selectedCustomerName}
              onExpand={() => expandGroup(group)}
              onFieldSelect={setFieldSelection}
              onSetRelationship={setRelationship}
              onSubmit={() => submitGroup(group, selectedCustomerId)}
              onPatientClick={onPatientClick}
              onAppointmentClick={onAppointmentClick}
            />
          ),
        )}
      </Box>
    </Box>
  );
};

export default DuplicatePatientGroupsList;
